import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {faEdit, faKey, faTrash, faUserAltSlash, faUserCheck} from '@fortawesome/free-solid-svg-icons';
import {User} from '../../../_data/admin/user';
import {UsersService} from '../../../_services/data/users.service';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {containsIgnoreCase, reformatDate} from '../../../_utils/lang';
import {HttpResponse} from '@angular/common/http';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {GetId} from '../../../_utils/get-id';
import {AuthenticationService} from '../../../_services/authentication.service';
import {exists} from '../../../_utils/operators';
import {GuiPermissionsService} from '../../../_services/gui/gui-permissions.service';
import {ItemListComponent} from '../../common/item-list/item-list.component';
import {SortableDirective} from '../../../_directives/sortable.directive';
import {RouterLink} from '@angular/router';
import {IconButtonComponent} from '../../../common/icon-button/icon-button.component';
import {AsyncPipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    imports: [ItemListComponent, SortableDirective, RouterLink, IconButtonComponent, AsyncPipe, TranslateModule]
})
export class UserListComponent extends GetId implements OnInit {

  @Input() caption: string;

  @Input() usersSubject: BehaviorSubject<User[]>;

  @Input() addNewRouterLink: any[] | string;
  @Input() addNewRouterState?: {[k: string]: any};
  @Input() canAddNew$: Observable<boolean> = of(false);

  @Input() searchMode: 'filter' | 'manual' = 'filter';
  @Output('manualSearch') _manualSearch: EventEmitter<string> = new EventEmitter<string>();

  reformatDate = reformatDate;

  faKey = faKey as IconProp;
  faEdit = faEdit as IconProp;
  faUserCheck = faUserCheck as IconProp;
  faUserAltSlash = faUserAltSlash as IconProp;
  faTrash = faTrash as IconProp;

  userListId = this.getId();

  filteredSortedUsers: User[];

  readonly usersService = inject(UsersService);
  readonly authService = inject(AuthenticationService);
  readonly guiPermissionsService = inject(GuiPermissionsService);

  private onUserUpdated(user: User) {
    /* When a user is updated, we want to refresh the table.
     *  1 - Grab latest data from the API for the user in question
     *  2 - Update the table with that information (but only for the given user, other users will remain as they were)
     */
    this.usersService.getUser(user.userName).subscribe((response: HttpResponse<User>) => {
      if(!!response.body) {
        this.usersSubject.next(this.usersSubject.value.map(u => u.userName === user.userName ? response.body : u));
      } else {
        //This can happen for older users that have data issues
        console.warn(`Response body null for refreshing user details of ${user.userName}!`, response);
      }
    });
  }

  private updateUserIfModified(user: User, confirmModification$: Observable<HttpResponse<string> | boolean>) {
    //Pipe the observable through exists, which will catch null and boolean false.
    confirmModification$.pipe(exists()).subscribe(() => this.onUserUpdated(user));
  }

  ngOnInit() {
  }

  userMatchesFilter(_filter: string, u: User) {
    return containsIgnoreCase(u.userName, _filter) || containsIgnoreCase(`${u.firstName} ${u.lastName}`, _filter);
  }

  onFilterSortUpdate($event: []) {
    this.filteredSortedUsers = $event;
  }

  disable(user: User, ariaLabelledBy: string, container: HTMLElement) {
    this.updateUserIfModified(user, this.usersService.confirmDisableUser(user, ariaLabelledBy, container));
  }

  enable(user: User, ariaLabelledBy: string, container: HTMLElement) {
    this.updateUserIfModified(user, this.usersService.confirmEnableUser(user, ariaLabelledBy, container));
  }

  reset(user: User, ariaLabelledBy: string, container: HTMLElement) {
    this.updateUserIfModified(user, this.usersService.confirmResetUserPassword(user, ariaLabelledBy, container));
  }

  terminate(user: User, ariaLabelledBy: string, container: HTMLElement) {
    //This one won't use this.updateUserIfModified() because it removes the user from the list rather than updating it.
    this.usersService.confirmTerminateUser(user, ariaLabelledBy, container).subscribe(success => {
      if(success) {
        this.usersSubject.next(this.usersSubject.value.filter(u => u.userName !== user.userName));
      }
    });
  }

}
